import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useMyOrganization, useCreditsInfo, useUpdateOrganization } from '../features/organizations';
import { usePlans } from '../features/subscriptions';
import { useUser } from '../features/auth/useUser';
import { formatDateTime as formatDate } from '../utils/formatters';
import { absoluteAppUrl } from '../utils/navigation';
import './ProfilePage.css';
import './OrganizationManagementPage.css';
import '../styles/badges.css';

type EditingField = 'display_name' | 'slug' | 'description' | 'billing_email' | null;

export default function OrganizationManagementPage() {
  const organizationQuery = useMyOrganization();
  const creditsQuery = useCreditsInfo();
  const plansQuery = usePlans();
  const updateOrganization = useUpdateOrganization();
  const { isAdmin } = useUser();

  const [copied, setCopied] = useState(false);
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const organization = organizationQuery.data;
  const credits = creditsQuery.data;
  // 現在契約中のプラン (Stripe 非依存の /plans から取得。is_current フラグで特定)
  const currentPlan = plansQuery.data?.plans.find((p) => p.is_current);

  // /org/:slug は SPA ルートのため、サブパス配信 (VITE_APP_BASENAME=/app) の
  // パートナーでは basename を前置しないと共有先で 404 になる。
  const slugUrlPrefix = `${absoluteAppUrl('/org')}/`;
  const slugUrl = organization?.slug ? `${slugUrlPrefix}${organization.slug}` : '';

  const handleCopySlugUrl = async () => {
    if (!slugUrl) return;
    try {
      await navigator.clipboard.writeText(slugUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleStartEdit = (field: EditingField) => {
    if (!organization || !field) return;

    setError(null);
    setEditingField(field);

    switch (field) {
      case 'display_name':
        setEditValue(organization.display_name || '');
        break;
      case 'slug':
        setEditValue(organization.slug || '');
        break;
      case 'description':
        setEditValue(organization.description || '');
        break;
      case 'billing_email':
        setEditValue(organization.billing_email || '');
        break;
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingField) return;

    setError(null);

    try {
      await updateOrganization.mutateAsync({
        [editingField]: editValue || (editingField === 'slug' ? null : editValue),
      });
      setEditingField(null);
      setEditValue('');
    } catch (err: any) {
      setError(err.response?.data?.error || '更新に失敗しました');
    }
  };

  // Escキーで編集をキャンセル
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && editingField) {
        handleCancelEdit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingField]);

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/office_building_double.svg" alt="" className="page-title-icon" />
            組織情報
          </h1>
          <p className="page-description">組織の情報を閲覧・編集します</p>
        </div>
      </div>

      {organizationQuery.isLoading && (
        <div className="admin-loading">
          組織情報を読み込み中...
        </div>
      )}

      {organizationQuery.isError && (
        <div className="admin-error">
          組織情報の読み込みに失敗しました
        </div>
      )}

      {organization && (
        <div className="profile-two-col">
          <div>
          {/* 組織情報 */}
          <h2 className="profile-section-title">組織情報</h2>
          <div className="profile-table-container">
            <table className="profile-table">
              <tbody>
                {/* 組織名 */}
                <tr>
                  <td className="profile-label">組織名</td>
                  <td className="profile-value">
                    {editingField === 'display_name' ? (
                      <div className="profile-edit-form">
                        <input
                          type="text"
                          className="form-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="組織名を入力"
                          autoFocus
                        />
                        <div className="form-actions-inline">
                          <button
                            type="button"
                            className="btn-save"
                            onClick={handleSaveEdit}
                            disabled={updateOrganization.isPending}
                          >
                            {updateOrganization.isPending ? '保存中...' : '保存'}
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={handleCancelEdit}
                            disabled={updateOrganization.isPending}
                          >
                            キャンセル
                          </button>
                        </div>
                        {error && <div className="edit-error">{error}</div>}
                      </div>
                    ) : (
                      <div className="profile-value-with-edit">
                        <span style={{ fontWeight: 600 }}>
                          {organization.display_name || organization.name || (
                            <span className="empty-value">未設定</span>
                          )}
                        </span>
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn-icon-edit"
                            onClick={() => handleStartEdit('display_name')}
                            title="編集"
                          >
                            <img src="/pencil_1.svg" alt="編集" style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>

                {/* 組織説明 */}
                <tr>
                  <td className="profile-label">組織説明</td>
                  <td className="profile-value">
                    {editingField === 'description' ? (
                      <div className="profile-edit-form">
                        <textarea
                          className="form-input edit-textarea"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="組織の説明を入力"
                          autoFocus
                        />
                        <div className="form-actions-inline">
                          <button
                            type="button"
                            className="btn-save"
                            onClick={handleSaveEdit}
                            disabled={updateOrganization.isPending}
                          >
                            {updateOrganization.isPending ? '保存中...' : '保存'}
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={handleCancelEdit}
                            disabled={updateOrganization.isPending}
                          >
                            キャンセル
                          </button>
                        </div>
                        {error && <div className="edit-error">{error}</div>}
                      </div>
                    ) : (
                      <div className="profile-value-with-edit">
                        <span>
                          {organization.description || <span className="empty-value">未設定</span>}
                        </span>
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn-icon-edit"
                            onClick={() => handleStartEdit('description')}
                            title="編集"
                          >
                            <img src="/pencil_1.svg" alt="編集" style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>

                {/* 組織ID */}
                <tr>
                  <td className="profile-label">組織ID</td>
                  <td className="profile-value">
                    <code className="code-value">{organization.id}</code>
                  </td>
                </tr>

                {/* スラッグURL */}
                <tr>
                  <td className="profile-label">スラッグURL</td>
                  <td className="profile-value">
                    {editingField === 'slug' ? (
                      <div className="edit-field-container">
                        <div className="slug-input-container">
                          <span className="slug-prefix">{slugUrlPrefix}</span>
                          <input
                            type="text"
                            className="edit-input slug-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value.toLowerCase())}
                            placeholder="your-organization"
                            autoFocus
                          />
                        </div>
                        <div className="slug-hint">
                          小文字英数字とハイフンのみ（3〜50文字）
                        </div>
                        <div className="edit-actions">
                          <button
                            type="button"
                            className="edit-save-btn"
                            onClick={handleSaveEdit}
                            disabled={updateOrganization.isPending}
                          >
                            {updateOrganization.isPending ? '保存中...' : '保存'}
                          </button>
                          <button
                            type="button"
                            className="edit-cancel-btn"
                            onClick={handleCancelEdit}
                            disabled={updateOrganization.isPending}
                          >
                            キャンセル
                          </button>
                        </div>
                        {error && <div className="edit-error">{error}</div>}
                      </div>
                    ) : (
                      <div className="profile-value-with-edit">
                        {organization.slug ? (
                          <>
                            <span className="slug-url-value">{slugUrl}</span>
                            <button
                              type="button"
                              className="copy-button"
                              onClick={handleCopySlugUrl}
                              title="URLをコピー"
                            >
                              {copied ? (
                                <img src="/check_double.svg" alt="コピー完了" className="copy-icon copied" />
                              ) : (
                                <img src="/duplicate.svg" alt="コピー" className="copy-icon" />
                              )}
                            </button>
                          </>
                        ) : (
                          <span className="empty-value">未設定</span>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn-icon-edit"
                            onClick={() => handleStartEdit('slug')}
                            title="編集"
                          >
                            <img src="/pencil_1.svg" alt="編集" style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>

                {/* 契約プラン */}
                <tr>
                  <td className="profile-label">契約プラン</td>
                  <td className="profile-value">
                    <Link to="/subscription" className="profile-link-value">
                      <span>
                        {currentPlan
                          ? currentPlan.display_name
                          : plansQuery.isLoading
                            ? '読み込み中...'
                            : 'プランを確認'}
                      </span>
                      <ChevronRight size={16} className="profile-link-chevron" />
                    </Link>
                  </td>
                </tr>

                {/* 請求先メール */}
                <tr>
                  <td className="profile-label">請求先メール</td>
                  <td className="profile-value">
                    {editingField === 'billing_email' ? (
                      <div className="profile-edit-form">
                        <input
                          type="email"
                          className="form-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="billing@example.com"
                          autoFocus
                        />
                        <div className="form-actions-inline">
                          <button
                            type="button"
                            className="btn-save"
                            onClick={handleSaveEdit}
                            disabled={updateOrganization.isPending}
                          >
                            {updateOrganization.isPending ? '保存中...' : '保存'}
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={handleCancelEdit}
                            disabled={updateOrganization.isPending}
                          >
                            キャンセル
                          </button>
                        </div>
                        {error && <div className="edit-error">{error}</div>}
                      </div>
                    ) : (
                      <div className="profile-value-with-edit">
                        <span>{organization.billing_email || <span className="empty-value">未設定</span>}</span>
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn-icon-edit"
                            onClick={() => handleStartEdit('billing_email')}
                            title="編集"
                          >
                            <img src="/pencil_1.svg" alt="編集" style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>

                {/* 作成日時 */}
                <tr>
                  <td className="profile-label">作成日時</td>
                  <td className="profile-value">{formatDate(organization.created_at)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>

          <div>
          {/* ポイント状況 */}
          <h2 className="profile-section-title">ポイント状況</h2>
          <div className="profile-table-container">
            <table className="profile-table">
              <tbody>
                <tr>
                  <td className="profile-label">ポイント残高（合計）</td>
                  <td className="profile-value" style={{ fontSize: '18px', fontWeight: 600 }}>
                    {Math.floor(credits?.total_available ?? organization.credits_balance).toLocaleString()} pt
                  </td>
                </tr>
                <tr>
                  <td className="profile-label">総購入ポイント</td>
                  <td className="profile-value">
                    {credits ? Math.floor(credits.total_purchased).toLocaleString() : Math.floor(organization.total_credits_purchased || 0).toLocaleString()} pt
                  </td>
                </tr>
                <tr>
                  <td className="profile-label">総消費ポイント</td>
                  <td className="profile-value">
                    {credits ? Math.floor(credits.total_consumed).toLocaleString() : Math.floor(organization.total_credits_consumed || 0).toLocaleString()} pt
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
